/**
 * @docs: docs/core/architecture/SCOUT_GATEWAY_REMOTE.md
 *
 * My Devices › an SG, laid out for the skipper (Tony, 2026-10-06: the stacked
 * cards were hard to follow): Open SG first and big; three tiles that say at a
 * glance how the software, the people and remote use stand; then one tab at a
 * time: People, Remote use, Software, Details. A tile opens its tab. The
 * registration code, serial and ids live under Details.
 */
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { OpenSgButton } from './OpenSgButton';
import { SgPeopleCard } from './SgPeopleCard';
import { SgRemoteDataCard, mb } from './SgRemoteDataCard';
import { SgSoftwareCard } from './SgSoftwareCard';
import { SgGuestLinks } from './SgGuestLinks';

type Tab = 'people' | 'remote' | 'software' | 'details';

const TABS: { id: Tab; label: string }[] = [
  { id: 'people', label: 'People' },
  { id: 'remote', label: 'Remote use' },
  { id: 'software', label: 'Software' },
  { id: 'details', label: 'Details' },
];

function Tile({ label, value, note, tone, onClick }: {
  label: string; value: string; note?: string; tone?: 'good' | 'warn'; onClick: () => void;
}) {
  const noteColour = tone === 'warn' ? 'text-amber-300' : tone === 'good' ? 'text-green-400' : 'text-slate-400';
  return (
    <button type="button" onClick={onClick}
      className="text-left bg-slate-700/50 hover:bg-slate-700 rounded-lg p-3 min-w-0 transition-colors">
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-white font-medium mt-1 truncate">{value}</p>
      {note && <p className={`text-xs mt-0.5 truncate ${noteColour}`}>{note}</p>}
    </button>
  );
}

export function SgDevicePanel({ deviceId, boat, details }: { deviceId: string; boat: string; details: ReactNode }) {
  const [tab, setTab] = useState<Tab>('people');
  // The same queries the tabs use, so the tiles and the tabs share one answer.
  const software = useQuery({ queryKey: ['gateway-software', deviceId], queryFn: () => api.getGatewaySoftware(deviceId), staleTime: 60_000 });
  const people = useQuery({ queryKey: ['gateway-people', deviceId], queryFn: () => api.getGatewayPeople(deviceId), retry: false });
  const usage = useQuery({ queryKey: ['gateway-remote-usage', deviceId], queryFn: () => api.getGatewayRemoteUsage(deviceId), retry: false });

  const s = software.data;
  const waiting = people.data?.requests.length ?? 0;
  const members = people.data?.members.filter((m) => !m.expired).length ?? 0;
  const u = usage.data;
  const capped = u && u.cap_bytes !== null && u.bytes >= u.cap_bytes;

  return (
    <div className="space-y-4" data-testid="sg-panel">
      <OpenSgButton deviceId={deviceId} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Tile label="Software" onClick={() => setTab('software')}
          value={s?.version ? `SG ${s.version}` : software.isLoading ? 'Checking…' : 'Not reported'}
          note={s ? (s.update ? `SG ${s.update.version} ready` : s.version ? 'Up to date' : undefined) : undefined}
          tone={s?.update ? 'warn' : 'good'} />
        <Tile label="People" onClick={() => setTab('people')}
          value={people.data ? (members === 0 ? 'Only you' : `You + ${members}`) : people.isLoading ? 'Checking…' : '—'}
          note={waiting > 0 ? `${waiting} asking to join` : undefined} tone={waiting > 0 ? 'warn' : undefined} />
        <Tile label="Remote use" onClick={() => setTab('remote')}
          value={u ? `${mb(u.bytes)} this month` : usage.isLoading ? 'Checking…' : '—'}
          note={u ? (capped ? 'Cap reached' : u.cap_bytes !== null ? `cap ${mb(u.cap_bytes)}` : 'No cap') : undefined}
          tone={capped ? 'warn' : undefined} />
      </div>

      <div>
        <div role="tablist" aria-label="Scout Gateway" className="flex gap-1 border-b border-slate-700 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
                tab === t.id ? 'border-cyan-400 text-white' : 'border-transparent text-slate-400 hover:text-white'
              }`}>
              {t.label}
              {t.id === 'people' && waiting > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-amber-500 text-slate-900 text-xs font-bold">{waiting}</span>
              )}
            </button>
          ))}
        </div>
        <div role="tabpanel" className="pt-3 max-h-[45vh] overflow-y-auto space-y-4">
          {tab === 'people' && <><SgGuestLinks deviceId={deviceId} boat={boat} /><SgPeopleCard deviceId={deviceId} /></>}
          {tab === 'remote' && <SgRemoteDataCard deviceId={deviceId} />}
          {tab === 'software' && <SgSoftwareCard deviceId={deviceId} />}
          {tab === 'details' && details}
        </div>
      </div>
    </div>
  );
}
